import React, { useState } from "react";
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import AuthPage from "./AuthPage";
import logoMark from "../assets/logo-mark.png";

const AUTH_BG = "https://images.unsplash.com/photo-1536138746221-2c489206ff6c?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDZ8MHwxfHNlYXJjaHwyfHxhYnN0cmFjdCUyMGx1eHVyeSUyMHRleHR1cmV8ZW58MHx8fGJsYWNrfDE3ODY4Mjk4MDh8MA&ixlib=rb-4.1.0&q=85";

export default function AuthScreen({ mode: initialMode = "login" }) {
  const [mode, setMode] = useState(initialMode);
  const isRegister = mode === "register";

  return (
    <div className="min-h-screen w-full grain relative" style={{ background: "var(--bg-base)" }} data-testid="auth-screen">
      {/* Back to landing */}
      <Link to="/" data-testid="auth-back-btn"
        className="absolute z-20 flex items-center gap-1.5 text-sm rounded-full px-3 py-2"
        style={{
          top: "calc(env(safe-area-inset-top) + 14px)", left: 16,
          background: "var(--overlay)", color: "var(--text-secondary)", border: "1px solid var(--border)",
        }}>
        <ArrowLeft size={16} /> Back
      </Link>

      <div className="lg:grid lg:grid-cols-2 min-h-screen">
        {/* Left brand panel — desktop only */}
        <div className="hidden lg:block relative overflow-hidden" data-testid="auth-brand-panel">
          <img src={AUTH_BG} alt="" className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0" style={{ background: "linear-gradient(120deg, rgba(11,11,18,0.86), rgba(11,11,18,0.62))" }} />
          <div className="relative z-10 h-full flex flex-col justify-between p-14">
            <div className="flex items-center gap-3">
              <div className="h-11 w-11 rounded-xl grid place-items-center gold-border" style={{ background: "rgba(20,20,31,0.6)" }}>
                <img src={logoMark} alt="The Family Vault" className="h-8 w-8 object-contain" />
              </div>
              <span className="font-serif-display text-2xl">The Family Vault</span>
            </div>
            <div>
              <h2 className="font-serif-display text-5xl leading-[1.05] max-w-md">
                A private home for everything your family holds dear.
              </h2>
              <p className="mt-5 text-base max-w-sm" style={{ color: "var(--text-secondary)" }}>
                Lineage, gatherings, memories and everyday moments — kept together, kept private.
              </p>
            </div>
            <div className="text-xs" style={{ color: "var(--text-muted)" }}>
              © {new Date().getFullYear()} The Family Vault
            </div>
          </div>
        </div>

        {/* Form column */}
        <div className="relative z-10 flex items-center justify-center px-6 py-20 min-h-screen">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="w-full max-w-md"
          >
            {/* Centered brand mark + heading */}
            <div className="text-center">
              <div className="h-14 w-14 mx-auto rounded-2xl grid place-items-center gold-border mb-6"
                style={{ background: "var(--bg-elevated)", boxShadow: "0 0 24px var(--shell-glow)" }}>
                <img src={logoMark} alt="The Family Vault" className="h-10 w-10 object-contain" />
              </div>
              <h1 className="font-serif-display text-4xl sm:text-5xl leading-none" data-testid="auth-heading">
                {isRegister ? "Create your account" : "Welcome back"}
              </h1>
              <p className="mt-3 text-sm" style={{ color: "var(--text-secondary)" }}>
                {isRegister
                  ? "Start your family's private vault in minutes."
                  : "Sign in to your family's private vault."}
              </p>
            </div>
            <div className="mt-8">
              <AuthPage defaultMode={initialMode} hideBranding onModeChange={setMode} />
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
